import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '@/components/Icon';
import { Spinner } from '@/components/Spinner';
import { Pager } from '@/components/Pager';
import { GroupFilterSelect, UNGROUPED_LABEL } from '@/components/form/GroupFilterSelect';
import { SearchBox } from '@/components/form/SearchBox';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useGroupOptions } from '@/hooks/useGroupOptions';
import { useFlipAnimation } from '@/hooks/useFlipAnimation';
import { showToast } from '@/lib/toast';
import { formatBalance } from '@/lib/clientFormat';
import {
  fetchClientTests,
  fetchClientTestGroups,
  updateClientTest,
  type ClientLibraryTestRow,
  type ClientsPageMeta,
} from '@/api/clients';

/** Та же пауза, что и у "Выбрано" в мастере создания (StepTests.tsx) — успеть увидеть сам клик до того, как строка переедет. */
const ADD_DELAY_MS = 350;
/** Та же пауза, что и у схлопывания строки в StepTests.tsx — см. её докблок про гонку с раскладкой ниже. */
const REMOVE_DELAY_MS = 220;

/**
 * Полный каталог библиотечных тестов (не только подключённых) – как в
 * старой админке (CustomerTestScreen/CustomerTarifTableLayout): иконка,
 * название, группа + базовая цена справочно, переключатель вкл/выкл.
 * Подключение реально активирует тест в MT1 (см. докблок
 * CustomerController::updateTest()) – переключатель может занять заметное
 * время и уйти в ошибку. Стоимость для клиента редактируется на вкладке «Цены».
 * Постранично (см. докблок CustomerController::tests() в Laravel).
 *
 * Подключённые тесты собраны отдельным закреплённым блоком "Подключено" над
 * каталогом (та же идея, что "Выбрано" в StepTests.tsx мастера создания) —
 * видны сразу, независимо от того, на какой странице/с каким поиском сейчас
 * открыт каталог, и в самом каталоге больше не показываются повторно.
 * Подключённые тесты грузятся отдельным запросом (attached=1, без пагинации —
 * их обычно на порядки меньше всей библиотеки, см. FetchClientTestsParams).
 *
 * Переезд между блоками оптимистичный — не дожидается ответа MT1 (тот же
 * принцип, что был в исходном toggleTest() этого компонента: ответ реально
 * может занять секунды, ждать его для отображения — значит выглядеть
 * подвисшим/нереагирующим). Но и не мгновенный: тумблер реагирует сразу
 * (видно, что клик засчитан), а сам переезд — через ADD_DELAY_MS/
 * REMOVE_DELAY_MS, иначе строка исчезает из каталога в тот же момент, что и
 * клик, и непонятно, что вообще произошло. Если запрос всё же упал — переезд
 * откатывается (отменяется, если ещё не успел случиться, или тест
 * возвращается в прежний блок, если уже успел), с баннером ошибки.
 */
export function LibraryTestsPanel({ accountId }: { accountId: number }) {
  const [search, searchInput, setSearchInput] = useDebouncedSearch();
  const [group, setGroup] = useState('');
  const [page, setPage] = useState(1);
  const { groups, ungroupedValue } = useGroupOptions(
    () => fetchClientTestGroups(accountId, 'library'),
    [accountId],
  );
  const [tests, setTests] = useState<ClientLibraryTestRow[] | null>(null);
  const [meta, setMeta] = useState<ClientsPageMeta | null>(null);
  const [searching, setSearching] = useState(false);
  const [connectedTests, setConnectedTests] = useState<Map<number, ClientLibraryTestRow>>(new Map());
  // Тест уже кликнут в каталоге, тумблер уже включён, но переезд в
  // "Подключено" ещё не случился — идёт ADD_DELAY_MS (см. toggleOn).
  const [pendingAdds, setPendingAdds] = useState<Map<number, ClientLibraryTestRow>>(new Map());
  // Строка "Подключено" схлопывается по клику (см. toggleOff) — реально из
  // connectedTests убирается таймером removeTimers, независимо от ответа
  // MT1 (см. докблок компонента про оптимистичность).
  const [removingIds, setRemovingIds] = useState<Set<number>>(new Set());
  const [pendingId, setPendingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const removingRowRefs = useRef(new Map<number, HTMLLIElement>());
  const addTimers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const removeTimers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const mountedRef = useRef(true);

  useEffect(() => {
    const addTimersMap = addTimers.current;
    const removeTimersMap = removeTimers.current;
    return () => {
      mountedRef.current = false;
      addTimersMap.forEach((timer) => clearTimeout(timer));
      addTimersMap.clear();
      removeTimersMap.forEach((timer) => clearTimeout(timer));
      removeTimersMap.clear();
    };
  }, []);

  // Подключённые тесты — отдельно от постраничного/поискового каталога ниже,
  // не зависят от search/group/page (см. докблок компонента).
  useEffect(() => {
    let cancelled = false;
    fetchClientTests(accountId, { attached: true, perPage: 500 })
      .then(({ items }) => {
        if (cancelled) return;
        setConnectedTests(new Map(items.map((t) => [t.id, t])));
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить подключённые тесты.');
      });
    return () => {
      cancelled = true;
    };
  }, [accountId]);

  // Новый поиск/фильтр — начинаем с первой страницы (тот же приём, что ClientsPage.tsx).
  useEffect(() => {
    setPage(1);
  }, [search, group]);

  useEffect(() => {
    let cancelled = false;
    setSearching(true);
    fetchClientTests(accountId, { search: search || undefined, group: group || undefined, page })
      .then(({ items, meta: pageMeta }) => {
        if (!cancelled) {
          setTests(items);
          setMeta(pageMeta);
        }
      })
      .catch(() => {
        if (!cancelled) setError('Не удалось загрузить список тестов.');
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountId, search, group, page]);

  const connected = useMemo(() => Array.from(connectedTests.values()), [connectedTests]);
  const connectedIds = useMemo(() => connected.map((t) => t.id), [connected]);
  // FLIP тут нужен только блоку "Подключено" — при удалении оттуда оставшиеся
  // строки подъезжают наверх. Каталог ниже больше не переставляет строки
  // (см. исходный докблок про orderByRaw на сервере).
  const { setItemRef: setConnectedItemRef, resync: resyncConnectedPositions } = useFlipAnimation(connectedIds);

  // Уже подключённые тесты видны в блоке "Подключено" выше — повторно
  // показывать их же в каталоге незачем.
  const availableTests = tests?.filter((t) => !connectedTests.has(t.id)) ?? null;

  const toggleOn = (test: ClientLibraryTestRow) => {
    if (pendingId === test.id) return;
    setPendingId(test.id);
    setError(null);
    // Тумблер включается сразу (видно, что клик засчитан), но тест ещё
    // остаётся в каталоге — иначе строка пропадает в тот же момент, что и
    // клик, и непонятно, что произошло.
    setPendingAdds((prev) => new Map(prev).set(test.id, test));

    // Переезд — по фиксированному локальному таймеру, не по ответу MT1 (тот
    // может занять секунды) — тот же принцип, что и в toggleOff ниже.
    const timer = setTimeout(() => {
      addTimers.current.delete(test.id);
      setPendingAdds((prev) => {
        const next = new Map(prev);
        next.delete(test.id);
        return next;
      });
      setConnectedTests((prev) => new Map(prev).set(test.id, { ...test, active: true }));
    }, ADD_DELAY_MS);
    addTimers.current.set(test.id, timer);

    updateClientTest(accountId, test.id, { attached: true })
      .then(() => {
        if (mountedRef.current) showToast(`Тест «${test.name}» подключён`);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        const pendingTimer = addTimers.current.get(test.id);
        if (pendingTimer) {
          // Переезд ещё не случился — просто отменяем, тест остался в каталоге.
          clearTimeout(pendingTimer);
          addTimers.current.delete(test.id);
          setPendingAdds((prev) => {
            const next = new Map(prev);
            next.delete(test.id);
            return next;
          });
        } else {
          // Уже переехал в "Подключено" — возвращаем обратно в каталог.
          setConnectedTests((prev) => {
            const next = new Map(prev);
            next.delete(test.id);
            return next;
          });
        }
        setError(`Не удалось подключить тест «${test.name}».`);
      })
      .finally(() => {
        if (mountedRef.current) setPendingId(null);
      });
  };

  const toggleOff = (test: ClientLibraryTestRow) => {
    if (pendingId === test.id) return;
    setPendingId(test.id);
    setError(null);
    setRemovingIds((prev) => new Set(prev).add(test.id));

    const el = removingRowRefs.current.get(test.id);
    if (el) {
      const height = el.getBoundingClientRect().height;
      el.style.maxHeight = `${height}px`;
      el.style.overflow = 'hidden';
      // Форсируем reflow — иначе браузер применит следующий maxHeight сразу,
      // без анимации (тот же приём, что и в useFlipAnimation/StepTests.tsx).
      el.getBoundingClientRect();
      requestAnimationFrame(() => {
        el.style.transition = 'max-height 0.2s ease, opacity 0.2s ease, padding 0.2s ease';
        el.style.maxHeight = '0px';
        el.style.opacity = '0';
        el.style.paddingTop = '0px';
        el.style.paddingBottom = '0px';
      });
    }

    // Реальное удаление — по фиксированному локальному таймеру (в такт со
    // схлопыванием), НЕ по ответу MT1: тот может занять секунды, и если
    // ждать именно его, между "красиво схлопнулось" и "реально пропало из
    // Подключено/появилось в каталоге" образуется заметная пауза — а когда
    // ответ всё же приходит, обе стороны (Подключено сжимается, каталог
    // получает строку) меняются одним рывком. Раз строка уже схлопнулась,
    // событие отмены (catch ниже) при неудаче просто возвращает тест обратно.
    const timer = setTimeout(() => {
      removeTimers.current.delete(test.id);
      // CSS-схлопывание уже отработало и сдвинуло соседние строки чисто
      // reflow'ом, не через FLIP (orderKey тут не менялся) — без resync()
      // следующий reorder (сама реальная мутация ниже) сравнил бы текущие
      // позиции с устаревшим "до схлопывания" снимком и на мгновение
      // отбросил бы строки назад (см. докблок useFlipAnimation.resync()).
      resyncConnectedPositions();
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(test.id);
        return next;
      });
      setConnectedTests((prev) => {
        const next = new Map(prev);
        next.delete(test.id);
        return next;
      });
    }, REMOVE_DELAY_MS);
    removeTimers.current.set(test.id, timer);

    updateClientTest(accountId, test.id, { attached: false })
      .then(() => {
        if (mountedRef.current) showToast(`Тест «${test.name}» отключён`);
      })
      .catch(() => {
        if (!mountedRef.current) return;
        const pendingTimer = removeTimers.current.get(test.id);
        if (pendingTimer) {
          // Схлопывание ещё не долетело до конца — просто отменяем, тест остался подключён.
          clearTimeout(pendingTimer);
          removeTimers.current.delete(test.id);
          setRemovingIds((prev) => {
            const next = new Set(prev);
            next.delete(test.id);
            return next;
          });
          if (el) {
            el.style.transition = '';
            el.style.maxHeight = '';
            el.style.overflow = '';
            el.style.opacity = '';
            el.style.paddingTop = '';
            el.style.paddingBottom = '';
          }
        } else {
          // Уже переехал обратно в каталог — возвращаем в "Подключено".
          setConnectedTests((prev) => new Map(prev).set(test.id, test));
        }
        setError(`Не удалось отключить тест «${test.name}».`);
      })
      .finally(() => {
        if (mountedRef.current) setPendingId(null);
      });
  };

  return (
    <div>
      {connected.length > 0 && (
        <div className="card selected-tests">
          <div className="selected-tests__header">Подключено: {connected.length}</div>
          <ul className="test-list">
            {connected.map((test) => {
              const removing = removingIds.has(test.id);
              const busy = pendingId === test.id;
              const flipRef = setConnectedItemRef(test.id);
              return (
                <li
                  key={test.id}
                  ref={(el) => {
                    flipRef(el);
                    if (el) removingRowRefs.current.set(test.id, el);
                    else removingRowRefs.current.delete(test.id);
                  }}
                  className="test-list__item"
                >
                  <div className="test-list__icon">
                    <Icon name="testChecklist" size={16} />
                  </div>
                  <div className="test-list__body">
                    <div className="test-list__name">
                      {test.name}
                      {test.version && <span className="test-list__version"> · v{test.version}</span>}
                    </div>
                    <div className="test-list__category">
                      {test.category ?? UNGROUPED_LABEL} · базовая цена {formatBalance(test.basePrice)}
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={!removing}
                    aria-label={`${test.name}: подключён`}
                    className={`switch${busy ? ' switch--pending' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => toggleOff(test)}
                  >
                    <span className="switch__track" />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Пока каталог не загрузился впервые, поиск/фильтр показывать незачем — фильтровать ещё нечего. */}
      {tests !== null && (
        <div className="filter-row">
          <SearchBox value={searchInput} onChange={setSearchInput} loading={searching} />
          <GroupFilterSelect
            groups={groups}
            ungroupedValue={ungroupedValue}
            value={group}
            onChange={setGroup}
          />
        </div>
      )}

      {error && <p className="field-error">{error}</p>}

      {availableTests === null ? (
        <div className="panel-loading">
          <Spinner />
        </div>
      ) : availableTests.length === 0 ? (
        <p className="cell-muted">
          {tests?.length ? 'Все тесты на этой странице уже подключены.' : 'Тесты не найдены.'}
        </p>
      ) : (
        <>
          <ul className="test-list">
            {availableTests.map((test) => {
              const pending = pendingAdds.has(test.id);
              return (
                <li key={test.id} className="test-list__item">
                  <div className="test-list__icon">
                    <Icon name="testChecklist" size={16} />
                  </div>
                  <div className="test-list__body">
                    <div className="test-list__name">
                      {test.name}
                      {test.version && <span className="test-list__version"> · v{test.version}</span>}
                    </div>
                    <div className="test-list__category">
                      {test.category ?? UNGROUPED_LABEL} · базовая цена {formatBalance(test.basePrice)}
                    </div>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={pending}
                    aria-label={`${test.name}: ${pending ? 'подключается' : 'не подключён'}`}
                    className={`switch${pending ? ' switch--pending' : ''}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => toggleOn(test)}
                  >
                    <span className="switch__track" />
                  </button>
                </li>
              );
            })}
          </ul>

          {meta && meta.lastPage > 1 && (
            <div className="table-footer">
              <span className="table-footer__status">
                Страница {meta.currentPage} из {meta.lastPage} · всего {meta.total}
              </span>
              <Pager page={meta.currentPage} lastPage={meta.lastPage} onPageChange={setPage} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
